import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { usePageTransition } from '../../utils/pageTransitions';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { useAuth } from '../../contexts/AuthContext';
import { inventoryService } from '../../services/inventoryService';
import { emailService } from '../../services/emailService';
import LoginModal from '../administrative-dashboard/components/LoginModal';

const InventoryManagementDashboard = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [activeTab, setActiveTab] = useState('items');
  const [loading, setLoading] = useState(true);
  const { variants, transition } = usePageTransition();
  
  // Inventory Items State
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Suppliers State
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  
  // Alerts State
  const [alerts, setAlerts] = useState([]);
  
  // Modals
  const [showItemModal, setShowItemModal] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingSupplier, setEditingSupplier] = useState(null);

  const [itemFormData, setItemFormData] = useState({
    itemCode: '',
    itemName: '',
    category: '',
    currentQuantity: '',
    minimumQuantity: '',
    unitOfMeasure: '',
    storageLocation: '',
    preferredSupplierId: ''
  });

  const [supplierFormData, setSupplierFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    leadTimeDays: ''
  });

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        setShowLoginModal(true);
      } else {
        setShowLoginModal(false);
        loadData();
      }
    }
  }, [user, authLoading]);

  useEffect(() => {
    applyFilters();
  }, [items, selectedCategory, searchQuery]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [itemsData, suppliersData, alertsData] = await Promise.all([
        inventoryService?.getAllItems(),
        inventoryService?.getAllSuppliers(),
        inventoryService?.getPendingAlerts(),
      ]);
      
      // Map items to match component expectations
      const mappedItems = itemsData?.map(item => ({
        id: item?.id,
        itemCode: item?.itemCode,
        name: item?.itemName,
        category: item?.category,
        currentQuantity: item?.currentQuantity,
        minimumQuantity: item?.minimumQuantity,
        unitOfMeasure: item?.unitOfMeasure,
        location: item?.storageLocation,
        status: item?.alertLevel === 'critical' ? 'out_of_stock' : 
                item?.alertLevel === 'low' ? 'low_stock' : 'in_stock',
        supplierId: item?.preferredSupplierId,
      })) || [];
      
      // Map suppliers to match component expectations
      const mappedSuppliers = suppliersData?.map(supplier => ({
        id: supplier?.id,
        name: supplier?.companyName,
        companyName: supplier?.companyName,
        contactPerson: supplier?.contactPerson,
        email: supplier?.email,
        phone: supplier?.phone,
        leadTimeDays: supplier?.leadTimeDays,
      })) || [];
      
      setItems(mappedItems);
      setSuppliers(mappedSuppliers);
      setAlerts(alertsData);
    } catch (error) {
      console.error('Error loading inventory data:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = items;

    if (selectedCategory !== 'all') {
      filtered = filtered?.filter(item => item?.category === selectedCategory);
    }

    if (searchQuery) {
      filtered = filtered?.filter(item =>
        item?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        item?.itemCode?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    setFilteredItems(filtered);
  };

  const handleSendAlert = async (alertItem) => {
    try {
      const item = items?.find(i => i?.id === alertItem?.inventoryItemId);
      const supplier = suppliers?.find(s => s?.id === item?.supplierId);
      
      if (supplier?.email) {
        await emailService?.sendInventoryAlertEmail(
          {
            id: alertItem?.id,
            itemName: item?.name,
            itemCode: item?.itemCode,
            currentQuantity: item?.currentQuantity,
            minimumQuantity: item?.minimumQuantity,
            alertType: alertItem?.alertType,
            location: item?.location,
            supplierName: supplier?.name,
            message: alertItem?.message,
          },
          supplier?.email,
          supplier?.contactPerson || supplier?.name
        );
        
        await inventoryService?.acknowledgeAlert(alertItem?.id);
        alert('Alerte envoyée avec succès au fournisseur');
        loadData();
      } else {
        alert('Le fournisseur n\'a pas d\'adresse email');
      }
    } catch (error) {
      console.error('Error sending alert:', error);
      alert('Erreur lors de l\'envoi de l\'alerte');
    }
  };

  const categoryOptions = [
    { value: 'all', label: 'Toutes les catégories' },
    { value: 'office_supplies', label: 'Fournitures de bureau' },
    { value: 'vehicle_parts', label: 'Pièces de véhicules' },
    { value: 'construction_materials', label: 'Matériaux de construction' },
    { value: 'it_equipment', label: 'Équipement informatique' },
  ];

  const getStatusColor = (status) => {
    const colors = {
      in_stock: 'var(--color-success)',
      low_stock: 'var(--color-warning)',
      out_of_stock: 'var(--color-error)',
      discontinued: 'var(--color-muted-foreground)',
    };
    return colors?.[status] || 'var(--color-muted-foreground)';
  };

  const getStatusLabel = (status) => {
    const labels = {
      in_stock: 'En stock',
      low_stock: 'Stock faible',
      out_of_stock: 'Rupture',
      discontinued: 'Discontinué',
    };
    return labels?.[status] || status;
  };

  const handleOpenItemModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setItemFormData({
        itemCode: item?.itemCode || '',
        itemName: item?.name || '',
        category: item?.category || '',
        currentQuantity: item?.currentQuantity || '',
        minimumQuantity: item?.minimumQuantity || '',
        unitOfMeasure: item?.unitOfMeasure || '',
        storageLocation: item?.location || '',
        preferredSupplierId: item?.supplierId || ''
      });
    } else {
      setEditingItem(null);
      setItemFormData({
        itemCode: '',
        itemName: '',
        category: '',
        currentQuantity: '',
        minimumQuantity: '',
        unitOfMeasure: '',
        storageLocation: '',
        preferredSupplierId: ''
      });
    }
    setShowItemModal(true);
  };

  const handleCloseItemModal = () => {
    setShowItemModal(false);
    setEditingItem(null);
  };

  const handleSubmitItem = async (e) => {
    e?.preventDefault();

    try {
      if (editingItem) {
        await inventoryService?.updateItem(editingItem?.id, itemFormData);
      } else {
        await inventoryService?.createItem(itemFormData);
      }
      handleCloseItemModal();
      loadData();
    } catch (err) {
      alert('Erreur: ' + err?.message);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet article?')) return;

    try {
      await inventoryService?.deleteItem(itemId);
      loadData();
    } catch (err) {
      alert('Erreur: ' + err?.message);
    }
  };

  const handleOpenSupplierModal = (supplier = null) => {
    if (supplier) {
      setEditingSupplier(supplier);
      setSupplierFormData({
        companyName: supplier?.companyName || supplier?.company_name || '',
        contactPerson: supplier?.contactPerson || supplier?.contact_person || '',
        email: supplier?.email || '',
        phone: supplier?.phone || '',
        leadTimeDays: supplier?.leadTimeDays || supplier?.lead_time_days || ''
      });
    } else {
      setEditingSupplier(null);
      setSupplierFormData({
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        leadTimeDays: ''
      });
    }
    setShowSupplierModal(true);
  };

  const handleCloseSupplierModal = () => {
    setShowSupplierModal(false);
    setEditingSupplier(null);
  };

  const handleSubmitSupplier = async (e) => {
    e?.preventDefault();

    try {
      if (editingSupplier) {
        await inventoryService?.updateSupplier(editingSupplier?.id, supplierFormData);
      } else {
        await inventoryService?.createSupplier(supplierFormData);
      }
      handleCloseSupplierModal();
      loadData();
    } catch (err) {
      alert('Erreur: ' + err?.message);
    }
  };

  const handleDeleteSupplier = async (supplierId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce fournisseur?')) return;

    try {
      await inventoryService?.deleteSupplier(supplierId);
      loadData();
    } catch (err) {
      alert('Erreur: ' + err?.message);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <motion.div 
        className="container mx-auto px-4 pt-24 md:pt-28 pb-8"
        initial="initial"
        animate="animate"
        exit="exit"
        variants={variants}
        transition={transition}
      >
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Gestion des Stocks & Inventaire
              </h1>
              <p className="text-muted-foreground">
                Suivi des fournitures, alertes stock bas, et gestion fournisseurs
              </p>
            </div>
            <Button onClick={() => navigate('/protected-admin-dashboard')}>
              <Icon name="ArrowLeft" size={20} className="mr-2" />
              Retour au Dashboard
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
            <div className="bg-card rounded-xl p-4 md:p-6 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Total Articles</p>
                  <p className="text-2xl md:text-3xl font-bold text-foreground">{items?.length}</p>
                </div>
                <Icon name="Package" size={40} className="text-primary opacity-20" />
              </div>
            </div>
            <div className="bg-card rounded-xl p-4 md:p-6 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Stock Faible</p>
                  <p className="text-2xl md:text-3xl font-bold text-warning">
                    {items?.filter(i => i?.status === 'low_stock')?.length}
                  </p>
                </div>
                <Icon name="AlertTriangle" size={40} className="text-warning opacity-20" />
              </div>
            </div>
            <div className="bg-card rounded-xl p-4 md:p-6 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Rupture</p>
                  <p className="text-2xl md:text-3xl font-bold text-error">
                    {items?.filter(i => i?.status === 'out_of_stock')?.length}
                  </p>
                </div>
                <Icon name="XCircle" size={40} className="text-error opacity-20" />
              </div>
            </div>
            <div className="bg-card rounded-xl p-4 md:p-6 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Fournisseurs</p>
                  <p className="text-2xl md:text-3xl font-bold text-foreground">{suppliers?.length}</p>
                </div>
                <Icon name="Truck" size={40} className="text-primary opacity-20" />
              </div>
            </div>
          </div>

          {/* Alerts Banner */}
          {alerts?.length > 0 && (
            <div className="bg-warning/10 border border-warning rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={24} className="text-warning flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground mb-1">
                    {alerts?.length} Alerte{alerts?.length > 1 ? 's' : ''} en attente
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Des articles nécessitent une attention immédiate. Consultez l'onglet Alertes.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setActiveTab('alerts')}
                >
                  Voir les alertes
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-card rounded-xl shadow-premium border border-border">
          <div className="border-b border-border">
            <div className="flex gap-2 md:gap-4 p-3 md:p-4 overflow-x-auto">
              {[
                { id: 'items', label: 'Articles', icon: 'Package' },
                { id: 'suppliers', label: 'Fournisseurs', icon: 'Truck' },
                { id: 'alerts', label: 'Alertes', icon: 'Bell', badge: alerts?.length },
              ]?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-3 md:px-4 py-2 text-sm rounded-lg font-medium transition-colors whitespace-nowrap relative ${
                    activeTab === tab?.id
                      ? 'bg-primary text-white' :'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  {tab?.label}
                  {tab?.badge > 0 && (
                    <span className="absolute -top-1 -right-1 bg-error text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {tab?.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {/* Items Tab */}
            {activeTab === 'items' && (
              <div>
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                  <h2 className="text-xl font-bold text-foreground">Articles en Stock</h2>
                  <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                    <Select
                      options={categoryOptions}
                      value={selectedCategory}
                      onChange={setSelectedCategory}
                      className="w-full md:w-64"
                    />
                    <Button onClick={() => handleOpenItemModal()}>
                      <Icon name="Plus" size={20} className="mr-2" />
                      Nouvel Article
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredItems?.map((item) => (
                    <div
                      key={item?.id}
                      className="bg-card rounded-xl shadow-premium border border-border p-6 hover:shadow-lg transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-foreground mb-1">{item?.name}</h3>
                          <p className="text-sm text-muted-foreground">{item?.itemCode}</p>
                        </div>
                        <span
                          className="px-3 py-1 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: getStatusColor(item?.status) + '20',
                            color: getStatusColor(item?.status)
                          }}
                        >
                          {getStatusLabel(item?.status)}
                        </span>
                      </div>

                      <div className="space-y-3 mb-4">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Catégorie:</span>
                          <span className="text-sm font-medium text-foreground">{item?.category}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Quantité:</span>
                          <span className="text-sm font-medium text-foreground">
                            {item?.currentQuantity} {item?.unitOfMeasure}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Minimum:</span>
                          <span className="text-sm font-medium text-foreground">
                            {item?.minimumQuantity} {item?.unitOfMeasure}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Emplacement:</span>
                          <span className="text-sm font-medium text-foreground">{item?.location}</span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenItemModal(item)}
                          className="flex-1"
                        >
                          <Icon name="Edit" size={16} />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteItem(item?.id)}
                          className="flex-1 text-error hover:bg-error/10"
                        >
                          <Icon name="Trash2" size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Suppliers Tab */}
            {activeTab === 'suppliers' && (
              <div>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
                  <h2 className="text-xl font-bold text-foreground">Fournisseurs</h2>
                  <Button onClick={() => handleOpenSupplierModal()}>
                    <Icon name="Plus" size={20} className="mr-2" />
                    Nouveau Fournisseur
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {suppliers?.map((supplier) => (
                    <div
                      key={supplier?.id}
                      className="bg-muted/50 rounded-xl p-6 hover:bg-muted transition-colors"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <h3 className="text-lg font-semibold text-foreground">{supplier?.name}</h3>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenSupplierModal(supplier)}
                          >
                            <Icon name="Edit" size={16} />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteSupplier(supplier?.id)}
                            className="text-error hover:bg-error/10"
                          >
                            <Icon name="Trash2" size={16} />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Icon name="User" size={16} className="text-muted-foreground" />
                          <span className="text-foreground">{supplier?.contactPerson}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon name="Mail" size={16} className="text-muted-foreground" />
                          <span className="text-foreground">{supplier?.email}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon name="Phone" size={16} className="text-muted-foreground" />
                          <span className="text-foreground">{supplier?.phone}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon name="Clock" size={16} className="text-muted-foreground" />
                          <span className="text-foreground">Délai: {supplier?.leadTimeDays} jours</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Alerts Tab */}
            {activeTab === 'alerts' && (
              <div>
                <h2 className="text-xl font-bold text-foreground mb-6">Alertes Stock</h2>
                <div className="space-y-4">
                  {alerts?.map((alert) => {
                    const item = items?.find(i => i?.id === alert?.inventoryItemId);
                    return (
                      <div
                        key={alert?.id}
                        className="bg-warning/10 border border-warning rounded-xl p-4"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Icon name="AlertTriangle" size={20} className="text-warning" />
                              <h3 className="font-semibold text-foreground">{item?.name}</h3>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">{alert?.message}</p>
                            <div className="flex gap-4 text-sm">
                              <span className="text-foreground">
                                <strong>Code:</strong> {item?.itemCode}
                              </span>
                              <span className="text-foreground">
                                <strong>Quantité:</strong> {item?.currentQuantity}
                              </span>
                              <span className="text-foreground">
                                <strong>Minimum:</strong> {item?.minimumQuantity}
                              </span>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSendAlert(alert)}
                          >
                            <Icon name="Send" size={16} className="mr-2" />
                            Envoyer au fournisseur
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  {alerts?.length === 0 && (
                    <div className="text-center py-12">
                      <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-4" />
                      <p className="text-muted-foreground">Aucune alerte en attente</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
      <Footer />
      {showLoginModal && (
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => navigate('/')}
          onLogin={() => setShowLoginModal(false)}
        />
      )}
      {/* Item Modal */}
      {showItemModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-premium max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-foreground">
                  {editingItem ? 'Modifier l\'Article' : 'Nouvel Article'}
                </h3>
                <button
                  onClick={handleCloseItemModal}
                  className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmitItem} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Code article *"
                  value={itemFormData?.itemCode}
                  onChange={(e) => setItemFormData({ ...itemFormData, itemCode: e?.target?.value })}
                  required
                />
                <Input
                  label="Nom de l'article *"
                  value={itemFormData?.itemName}
                  onChange={(e) => setItemFormData({ ...itemFormData, itemName: e?.target?.value })}
                  required
                />
                <Input
                  label="Catégorie *"
                  value={itemFormData?.category}
                  onChange={(e) => setItemFormData({ ...itemFormData, category: e?.target?.value })}
                  required
                />
                <Input
                  label="Quantité actuelle *"
                  type="number"
                  value={itemFormData?.currentQuantity}
                  onChange={(e) => setItemFormData({ ...itemFormData, currentQuantity: e?.target?.value })}
                  required
                />
                <Input
                  label="Quantité minimum *"
                  type="number"
                  value={itemFormData?.minimumQuantity}
                  onChange={(e) => setItemFormData({ ...itemFormData, minimumQuantity: e?.target?.value })}
                  required
                />
                <Input
                  label="Unité de mesure *"
                  value={itemFormData?.unitOfMeasure}
                  onChange={(e) => setItemFormData({ ...itemFormData, unitOfMeasure: e?.target?.value })}
                  required
                />
                <Input
                  label="Emplacement *"
                  value={itemFormData?.storageLocation}
                  onChange={(e) => setItemFormData({ ...itemFormData, storageLocation: e?.target?.value })}
                  required
                />
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Fournisseur préféré</label>
                  <Select
                    options={[
                      { value: '', label: 'Aucun' },
                      ...suppliers?.map(s => ({ value: s?.id, label: s?.name }))
                    ]}
                    value={itemFormData?.preferredSupplierId}
                    onChange={(value) => setItemFormData({ ...itemFormData, preferredSupplierId: value })}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 pt-4">
                <Button type="submit" variant="default" className="flex-1">
                  {editingItem ? 'Mettre à jour' : 'Créer'}
                </Button>
                <Button type="button" variant="outline" onClick={handleCloseItemModal} className="flex-1">
                  Annuler
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Supplier Modal */}
      {showSupplierModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-premium max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-foreground">
                  {editingSupplier ? 'Modifier le Fournisseur' : 'Nouveau Fournisseur'}
                </h3>
                <button
                  onClick={handleCloseSupplierModal}
                  className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmitSupplier} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nom du fournisseur *"
                  value={supplierFormData?.companyName}
                  onChange={(e) => setSupplierFormData({ ...supplierFormData, companyName: e?.target?.value })}
                  required
                />
                <Input
                  label="Personne de contact *"
                  value={supplierFormData?.contactPerson}
                  onChange={(e) => setSupplierFormData({ ...supplierFormData, contactPerson: e?.target?.value })}
                  required
                />
                <Input
                  label="Email *"
                  type="email"
                  value={supplierFormData?.email}
                  onChange={(e) => setSupplierFormData({ ...supplierFormData, email: e?.target?.value })}
                  required
                />
                <Input
                  label="Téléphone *"
                  value={supplierFormData?.phone}
                  onChange={(e) => setSupplierFormData({ ...supplierFormData, phone: e?.target?.value })}
                  required
                />
                <Input
                  label="Délai de livraison (jours) *"
                  type="number"
                  value={supplierFormData?.leadTimeDays}
                  onChange={(e) => setSupplierFormData({ ...supplierFormData, leadTimeDays: e?.target?.value })}
                  required
                />
              </div>

              <div className="flex items-center gap-3 pt-4">
                <Button type="submit" variant="default" className="flex-1">
                  {editingSupplier ? 'Mettre à jour' : 'Créer'}
                </Button>
                <Button type="button" variant="outline" onClick={handleCloseSupplierModal} className="flex-1">
                  Annuler
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryManagementDashboard;